import React from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import ScrollReveal from "../common/Reveal";
import getVisiblePages from "../../utils/productPagination";
import "./ProductsPagination.css";

function ProductsPagination({
  page,
  totalPages,
  total,
  displayedCount,
  hasMore,
  loadingMore = false,
  onShowMore,
  onPageChange,
}) {
  const { t } = useTranslation();
  const showPageNav = totalPages > 1;
  const showLoadMore = hasMore;
  const remainingCount = Math.max(0, total - displayedCount);
  const pageItems = getVisiblePages(page, totalPages);

  if (!showPageNav && !showLoadMore && total <= 0) return null;

  return (
    <ScrollReveal>
      <nav
        className="caf-products-pagination"
        aria-label={t("Products pagination")}
      >
        <div className="caf-products-pagination__panel">
          {total > 0 ? (
            <header className="caf-products-pagination__header">
              <p className="caf-products-pagination__summary">
                {t("products.showingCount", { shown: displayedCount, total })}
              </p>
              {showPageNav ? (
                <p className="caf-products-pagination__meta">
                  {t("products.pageOf", { page, totalPages })}
                </p>
              ) : null}
              <div
                className="caf-products-pagination__progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={displayedCount}
                aria-label={t("products.showingCount", { shown: displayedCount, total })}
              >
                <span
                  className="caf-products-pagination__progress-bar"
                  style={{ width: `${Math.min(100, (displayedCount / total) * 100)}%` }}
                />
              </div>
            </header>
          ) : null}

          {showPageNav ? (
            <section
              className="caf-products-pagination__section"
              aria-label={t("products.pageNavigation")}
            >
              <h3 className="caf-products-pagination__section-title">
                {t("products.pageNavigation")}
              </h3>

              <div className="caf-products-pagination__nav">
                <button
                  type="button"
                  className="caf-btn caf-btn--outline caf-products-pagination__nav-btn"
                  disabled={page <= 1 || loadingMore}
                  onClick={() => onPageChange(page - 1)}
                  aria-label={t("Previous")}
                >
                  <FaChevronLeft className="caf-products-pagination__nav-icon" aria-hidden="true" />
                  <span className="caf-products-pagination__nav-text">{t("Previous")}</span>
                </button>

                <ul className="caf-products-pagination__pages" role="list">
                  {pageItems.map((item, index) =>
                    item === "ellipsis" ? (
                      <li
                        key={`ellipsis-${index}`}
                        className="caf-products-pagination__ellipsis"
                        aria-hidden="true"
                      >
                        …
                      </li>
                    ) : (
                      <li key={item}>
                        <button
                          type="button"
                          className={`caf-products-pagination__page${
                            item === page ? " caf-products-pagination__page--active" : ""
                          }`}
                          onClick={() => onPageChange(item)}
                          disabled={loadingMore || item === page}
                          aria-current={item === page ? "page" : undefined}
                          aria-label={`${t("Page")} ${item}`}
                        >
                          {item}
                        </button>
                      </li>
                    ),
                  )}
                </ul>

                <button
                  type="button"
                  className="caf-btn caf-btn--outline caf-products-pagination__nav-btn"
                  disabled={page >= totalPages || loadingMore}
                  onClick={() => onPageChange(page + 1)}
                  aria-label={t("Next")}
                >
                  <span className="caf-products-pagination__nav-text">{t("Next")}</span>
                  <FaChevronRight className="caf-products-pagination__nav-icon" aria-hidden="true" />
                </button>
              </div>
            </section>
          ) : null}

          {showPageNav && showLoadMore ? (
            <div className="caf-products-pagination__divider" role="presentation" />
          ) : null}

          {showLoadMore ? (
            <section
              className="caf-products-pagination__section caf-products-pagination__section--load"
              aria-label={t("products.loadMoreSection")}
            >
              <div className="caf-products-pagination__load-copy">
                <h3 className="caf-products-pagination__section-title">
                  {t("products.loadMoreSection")}
                </h3>
                <p className="caf-products-pagination__hint">
                  {t("products.loadMoreHint", { count: Math.min(6, remainingCount) })}
                </p>
              </div>

              <button
                type="button"
                className="caf-btn caf-btn--primary caf-products-pagination__more"
                disabled={loadingMore}
                onClick={onShowMore}
              >
                {loadingMore ? (
                  <>
                    <span className="caf-products-pagination__spinner" aria-hidden="true" />
                    <span>{t("Loading products")}</span>
                  </>
                ) : (
                  t("Show more")
                )}
              </button>
            </section>
          ) : null}
        </div>
      </nav>
    </ScrollReveal>
  );
}

export default ProductsPagination;
